import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, Box } from "@mui/material";
import { api } from "../api";
import { iberia } from "../daiService";
import { type DashboardRow, type SlaStatus } from "./daiAdapters";
import type { FilterOption } from "../constants";
import DaiCustomSelectDropdown from "../components/DaiCustomSelectDropdown";
import StatusChip from "../components/StatusChip";
import { SummaryCard } from "../../Common/SummaryCards";
import Header from "../components/Header";
import Nav from "../components/Nav";
import DateInput from "../components/DateInput";

function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n: number) { return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10); }

const DOW_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SCHEDULE_OPTIONS = ["Expected Today", "Not Expected Today", "Schedule Unknown"];
const SCHEDULE_LABELS: Record<string, string> = { "Schedule Unknown": "Not Available" };
const SLA_OPTIONS = ["On Time", "Missed", "Not Due", "No SLA Time", "Not Applicable"];

const SCHEDULE_FILTER_OPTIONS: FilterOption[] = [
  { label: "All Schedules", value: "" },
  ...SCHEDULE_OPTIONS.map((o) => ({ label: SCHEDULE_LABELS[o] || o, value: o })),
];
const SLA_FILTER_OPTIONS: FilterOption[] = [
  { label: "All SLA Status", value: "" },
  ...SLA_OPTIONS.map((o) => ({ label: o, value: o })),
];

// Execution state derived from the unified SLA status when the backend does not
// send an explicit job_run_status for a dataset.
const RUN_STATUS_FROM_SLA: Record<string, string> = {
  success: "Completed", recovered: "Completed", delayed_ok: "Completed", missed_sla: "Completed",
  in_progress: "Running", delayed_running: "Running",
  failed: "Failed", cancelled: "Not Started",
  delayed: "Not Started", missed: "Not Started", scheduled: "Not Started", unknown: "Not Started",
};

function cls(name?: string | null): string { return String(name || "Unknown").replace(/\s+/g, ""); }

/** The backend reports a stage status of `unknown` when a stage has no runs for
 *  the selected date. Those stages are still listed in the chain, but without a
 *  meaningless "unknown" label. */
function hasStageStatus(status?: string | null): boolean {
  const s = String(status || "").trim().toLowerCase();
  return s !== "" && s !== "unknown" && s !== "n/a" && s !== "null" && s !== "undefined";
}

function fmtNum(n?: number | null): string {
  if (n == null) return "-";
  return Math.round(n).toLocaleString();
}
function fmtHrs(h?: number | null): string {
  if (h == null) return "-";
  return (h * 60).toFixed(0) + " min";
}

interface IberiaRow extends DashboardRow {
  source_dataset: string;
  schedule_status: string;
  job_run_status: string;
  sla_status: string;
  stages: any[];
}

/**
 * Flatten the backend's nested dataset rollup into rows. Datasets have no
 * data_asset/status fields of their own (those are DashboardRow leftovers) —
 * the real Iberia fields are source_dataset/dataset_status/schedule_status/
 * job_run_status/sla_status/stages, set directly from the raw dataset object.
 */
function toIberiaRows(date: string, datasets: any[] | undefined): IberiaRow[] {
  const raw = datasets ?? [];
  return raw.map((src: any): IberiaRow => ({
    data_asset: src.source_dataset || "",
    market: src.market || "",
    gdo: src.gdo || "",
    status: (src.dataset_status || "unknown") as SlaStatus,
    run_count: src.stages?.reduce((s: number, st: any) => s + (st.job_count || 0), 0) || 0,
    failed_count: 0,
    critical: false,
    source_dataset: src.source_dataset,
    schedule_status: src.schedule_status || "Schedule Unknown",
    job_run_status: src.job_run_status || RUN_STATUS_FROM_SLA[src.dataset_status as SlaStatus] || "Not Started",
    sla_status: src.sla_status || "Not Applicable",
    stages: src.stages || [],
  }));
}

interface CardFilter {
  type: string;
  value: string;
}

export default function Iberia() {
  const [date, setDate] = useState(todayIso());
  const [datasets, setDatasets] = useState<IberiaRow[]>([]);
  const [meta, setMeta] = useState<any>({});
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [scheduleFilter, setScheduleFilter] = useState("");
  const [timingFilter, setTimingFilter] = useState("");
  const [cardFilter, setCardFilter] = useState<CardFilter>({ type: "all", value: "" });
  const [expandedDataset, setExpandedDataset] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, any>>({});
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [jobDetailCache, setJobDetailCache] = useState<Record<string, any>>({});
  const [expandedRunsJobId, setExpandedRunsJobId] = useState<string | null>(null);
  const [jobRunsCache, setJobRunsCache] = useState<Record<string, any>>({});

  const load = useCallback(async (d: string = date) => {
    setIsLoading(true);
    const { ok, data, error: err } = await iberia.getStatus(d);
    if (!ok) {
      setError("Failed to load Iberia status: " + (err || "unknown error"));
    } else {
      setDatasets(toIberiaRows(data?.date ?? d, data?.datasets));
      setMeta({
        config_synced_at: (data as any)?.config_synced_at,
        logs_synced_at: (data as any)?.logs_synced_at,
      });
      setError("");
    }
    setIsLoading(false);
    setExpandedDataset(null);
    setExpandedJobId(null);
    setExpandedRunsJobId(null);
    setDetailCache({});
    setJobDetailCache({});
    setJobRunsCache({});
  }, [date]);

  useEffect(() => {
    load(date);
    const id = setInterval(() => load(date), 60000);
    return () => clearInterval(id);
  }, [date, load]);

  const summary = useMemo(() => {
    const counts: Record<string, number> = { Completed: 0, Running: 0, Failed: 0, "Not Started": 0, expectedToday: 0, missedSla: 0 };
    datasets.forEach((d) => {
      if (counts[d.job_run_status] !== undefined) counts[d.job_run_status]++;
      if (d.schedule_status === "Expected Today") counts.expectedToday++;
      if (d.sla_status === "Missed") counts.missedSla++;
    });
    return counts;
  }, [datasets]);

  const toggleCardFilter = useCallback((type: string, value: string) => {
    setCardFilter((cur) => (cur.type === type && cur.value === value ? { type: "all", value: "" } : { type, value }));
  }, []);

  const filteredDatasets = useMemo(() => {
    let rows = datasets;
    if (cardFilter.type === "run") rows = rows.filter((d) => d.job_run_status === cardFilter.value);
    if (cardFilter.type === "schedule") rows = rows.filter((d) => d.schedule_status === cardFilter.value);
    if (cardFilter.type === "sla") rows = rows.filter((d) => d.sla_status === cardFilter.value);
    if (scheduleFilter) rows = rows.filter((d) => (d.schedule_status || "Schedule Unknown") === scheduleFilter);
    if (timingFilter) rows = rows.filter((d) => (d.sla_status || "Not Applicable") === timingFilter);
    if (search) rows = rows.filter((d) => (d.source_dataset || "").toLowerCase().includes(search.toLowerCase()));
    return rows;
  }, [datasets, cardFilter, scheduleFilter, timingFilter, search]);

  const exportCsv = useCallback(() => {
    const headers = ["Dataset", "Schedule Status", "Job Run Status", "SLA Status", "Stage Chain"];
    const csvValue = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.map(csvValue).join(",")];
    filteredDatasets.forEach((d) => {
      const stageChain = (d.stages || []).map((s: any) => {
        const range = s.hist_avg_start && s.hist_avg_end ? ` ${s.hist_avg_start}->${s.hist_avg_end}` : "";
        const jobs = s.job_count > 1 ? ` (${s.job_count} jobs)` : "";
        return `${s.stage_name || ""}${hasStageStatus(s.status) ? `: ${s.status}` : ""}${jobs}${range}`;
      }).join(" | ");
      lines.push([d.source_dataset, d.schedule_status, d.job_run_status, d.sla_status || "Not Applicable", stageChain].map(csvValue).join(","));
    });
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `iberia_dataset_status_${date || todayIso()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [filteredDatasets, date]);

  const toggleDatasetDetail = useCallback(async (dataset: string) => {
    if (expandedDataset === dataset) { setExpandedDataset(null); setExpandedJobId(null); return; }
    setExpandedDataset(dataset);
    setExpandedJobId(null);
    if (!detailCache[dataset]) {
      const { ok, data, error: err } = await iberia.getDatasetDetail(dataset, date);
      setDetailCache((cur) => ({ ...cur, [dataset]: ok ? data : { error: err } }));
    }
  }, [expandedDataset, detailCache, date]);

  const toggleJobDetail = useCallback(async (jobId: string) => {
    if (expandedJobId === jobId) { setExpandedJobId(null); return; }
    setExpandedJobId(jobId);
    if (!jobDetailCache[jobId]) {
      const { ok, data: d, error: err } = await api.iberiaJobDetail(jobId);
      setJobDetailCache((cur) => ({ ...cur, [jobId]: ok ? d : { error: err } }));
    }
  }, [expandedJobId, jobDetailCache]);

  const toggleJobRuns = useCallback(async (jobId: string) => {
    if (expandedRunsJobId === jobId) { setExpandedRunsJobId(null); return; }
    setExpandedRunsJobId(jobId);
    if (!jobRunsCache[jobId]) {
      const { ok, data: d, error: err } = await api.iberiaJobRuns(jobId, date);
      setJobRunsCache((cur) => ({ ...cur, [jobId]: ok ? d : { error: err } }));
    }
  }, [expandedRunsJobId, jobRunsCache, date]);

  const refreshJobDetail = useCallback(async (jobId: string) => {
    const { ok, data: d, error: err } = await api.iberiaJobDetail(jobId);
    setJobDetailCache((cur) => ({ ...cur, [jobId]: ok ? d : { error: err } }));
  }, []);

  return (
    <>
      <Header
        right={
          <div className="refresh-info">
            <span id="last-refresh">
              {meta.config_synced_at || meta.logs_synced_at
                ? `Config synced: ${meta.config_synced_at ? new Date(meta.config_synced_at).toLocaleString() : "never"} | Logs synced: ${meta.logs_synced_at ? new Date(meta.logs_synced_at).toLocaleString() : "never"}`
                : "Loading\u2026"}
            </span>
          </div>
        }
      />
      <Nav />

      <div className="summary-bar" id="iberia-summary">
        <SummaryCard status="all" label="All Datasets" count={datasets.length} isActive={cardFilter.type === "all"} onClick={() => toggleCardFilter("all", "")} isLoading={isLoading} />
        <SummaryCard status="expected" label="Today's Datasets" count={summary.expectedToday} isActive={cardFilter.type === "schedule" && cardFilter.value === "Expected Today"} onClick={() => toggleCardFilter("schedule", "Expected Today")} isLoading={isLoading} />
        <SummaryCard status="completed" label="Completed" count={summary.Completed} isActive={cardFilter.type === "run" && cardFilter.value === "Completed"} onClick={() => toggleCardFilter("run", "Completed")} isLoading={isLoading} />
        <SummaryCard status="running" label="Running" count={summary.Running} isActive={cardFilter.type === "run" && cardFilter.value === "Running"} onClick={() => toggleCardFilter("run", "Running")} isLoading={isLoading} />
        <SummaryCard status="failed" label="Failed" count={summary.Failed} isActive={cardFilter.type === "run" && cardFilter.value === "Failed"} onClick={() => toggleCardFilter("run", "Failed")} isLoading={isLoading} />
        <SummaryCard status="missed" label="Missed SLA" count={summary.missedSla} isActive={cardFilter.type === "sla" && cardFilter.value === "Missed"} onClick={() => toggleCardFilter("sla", "Missed")} isLoading={isLoading} />
        <SummaryCard status="not-started" label="Not Started" count={summary["Not Started"]} isActive={cardFilter.type === "run" && cardFilter.value === "Not Started"} onClick={() => toggleCardFilter("run", "Not Started")} isLoading={isLoading} />
      </div>

      <div className="controls">
        <input type="text" id="iberia-search" placeholder="Search dataset..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <DaiCustomSelectDropdown options={SCHEDULE_FILTER_OPTIONS} value={scheduleFilter} handleChange={(e) => setScheduleFilter(e.target.value)} noSelectionLabel="Select Schedule" />
        <DaiCustomSelectDropdown options={SLA_FILTER_OPTIONS} value={timingFilter} handleChange={(e) => setTimingFilter(e.target.value)} noSelectionLabel="Select SLA Status" />
        <DateInput id="iberia-date-picker" value={date} min={daysAgoIso(30)} max={todayIso()} onChange={(e) => setDate(e.target.value || todayIso())} />
        <button className="btn-export" onClick={exportCsv}>&#8659; Export CSV</button>
        <span className="job-count" id="iberia-job-count">{filteredDatasets.length} datasets</span>
      </div>

      {error && <div style={{ color: "#c0392b", padding: "6px 20px" }}>{error}</div>}

      <div className="iberia-table-wrap">
        <table className="iberia-table">
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Dataset</th>
              <th>Schedule</th>
              <th>Job Run</th>
              <th>SLA</th>
              <th>Stage Chain (hist avg start&rarr;end)</th>
            </tr>
          </thead>
          <tbody id="iberia-tbody">
            {!datasets.length && !error ? (
              <tr><td colSpan={6}>Loading&hellip;</td></tr>
            ) : filteredDatasets.length === 0 ? (
              <tr><td colSpan={6}>No datasets found.</td></tr>
            ) : filteredDatasets.map((d) => (
              <Fragment key={d.source_dataset}>
                <tr className="dataset-row" onClick={() => toggleDatasetDetail(d.source_dataset)}>
                  <td><span className={`expand-icon ${expandedDataset === d.source_dataset ? "open" : ""}`}>&#9656;</span></td>
                  <td>{d.source_dataset}</td>
                  <td><StatusChip status={SCHEDULE_LABELS[d.schedule_status] || d.schedule_status} /></td>
                  <td><StatusChip status={d.job_run_status} /></td>
                  <td><StatusChip status={d.sla_status} /></td>
                  <td>
                    {(d.stages || []).map((s: any, i: number) => (
                      <span key={i} className={`stage-chip ${cls(s.status)}`}>
                        {s.stage_name}{hasStageStatus(s.status) ? `: ${s.status}` : ""}{s.job_count > 1 ? ` (${s.job_count} jobs)` : ""}
                        {s.hist_avg_start && s.hist_avg_end ? <span className="hist-time">{s.hist_avg_start}&rarr;{s.hist_avg_end}</span> : null}
                      </span>
                    ))}
                  </td>
                </tr>
                {expandedDataset === d.source_dataset && (
                  <tr className="detail-row">
                    <td colSpan={6}>
                      <DatasetDetailPanel
                        detail={detailCache[d.source_dataset]}
                        expandedJobId={expandedJobId}
                        expandedRunsJobId={expandedRunsJobId}
                        jobDetailCache={jobDetailCache}
                        jobRunsCache={jobRunsCache}
                        onToggleJob={toggleJobDetail}
                        onToggleRuns={toggleJobRuns}
                        onRefreshJobDetail={refreshJobDetail}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

interface DatasetDetailPanelProps {
  detail: any;
  expandedJobId: string | null;
  expandedRunsJobId: string | null;
  jobDetailCache: Record<string, any>;
  jobRunsCache: Record<string, any>;
  onToggleJob: (jobId: string) => void;
  onToggleRuns: (jobId: string) => void;
  onRefreshJobDetail: (jobId: string) => void;
}

function DatasetDetailPanel({ detail, expandedJobId, expandedRunsJobId, jobDetailCache, jobRunsCache, onToggleJob, onToggleRuns, onRefreshJobDetail }: DatasetDetailPanelProps) {
  if (!detail) return <span className="loading-note">Loading job details&hellip;</span>;
  if (detail.error) return <span className="loading-note">Failed to load: {detail.error}</span>;
  // daiService returns the jobs nested under stages; older payloads expose them flat.
  const jobs: any[] = detail.jobs
    || (detail.stages || []).flatMap((s: any) => (s.jobs || []).map((j: any) => ({ stage_name: s.stage_name, ...j })));

  return (
    <div className="detail-grid">
      <div className="detail-card jobs-card">
        <h4>Job Configuration &amp; Run Detail{" "}
          <span className="loading-note">(click a job row for weekly schedule &amp; volume; click the runs badge to see all runs today)</span>
        </h4>
        <table className="mini-table">
          <thead>
            <tr>
              <th>Job ID</th><th>Stage</th><th>Activity</th><th>Dest Dataset</th><th>Hist Start (UTC)</th><th>Hist End (UTC)</th>
              <th>Actual Start (UTC)</th><th>Actual End (UTC)</th><th>SLA</th><th>Run Status</th><th>Runs Today</th><th>Run ID</th><th>Rows</th>
              <th>Frequency</th><th>Scheduler</th><th>Load Type</th><th>Dest Container</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j: any) => (
              <Fragment key={j.job_id}>
                <tr className="job-row" onClick={() => onToggleJob(j.job_id)}>
                  <td><span className={`expand-icon ${expandedJobId === j.job_id ? "open" : ""}`}>&#9656;</span> {j.job_id}</td>
                  <td>{j.stage_name}</td>
                  <td>{j.activity_name || "-"}</td>
                  <td>{j.dest_dataset || ""}</td>
                  <td>{j.hist_avg_start || "-"}</td>
                  <td>{j.hist_avg_end || "-"}</td>
                  <td className="datetime-cell">{j.actual_start || "-"}</td>
                  <td className="datetime-cell">{j.actual_end || "-"}</td>
                  <td><StatusChip status={j.timing_status || "Not Scheduled"} /></td>
                  <td><StatusChip status={j.run_status || "Not Started"} /></td>
                  <td>
                    {j.run_count_today > 1 ? (
                      <span className="runs-badge multi" onClick={(e) => { e.stopPropagation(); onToggleRuns(j.job_id); }}>
                        {j.run_count_today} runs <span className={`expand-icon ${expandedRunsJobId === j.job_id ? "open" : ""}`}>&#9656;</span>
                      </span>
                    ) : (
                      <span className="runs-badge">{j.run_count_today || 0}</span>
                    )}
                  </td>
                  <td className="run-id-cell" title={j.run_id || ""}>{j.run_id || "-"}</td>
                  <td>{fmtNum(j.row_count)}</td>
                  <td>{j.frequency || "-"}</td>
                  <td>{j.scheduler_method || "-"}</td>
                  <td>{j.load_type || "-"}</td>
                  <td title={j.dest_path || ""}>{j.dest_container || "-"}</td>
                </tr>
                {expandedJobId === j.job_id && (
                  <tr className="job-detail-row"><td colSpan={17}>
                    <JobSideCards jobId={j.job_id} jd={jobDetailCache[j.job_id]} onSaved={() => onRefreshJobDetail(j.job_id)} />
                  </td></tr>
                )}
                {expandedRunsJobId === j.job_id && (
                  <tr className="job-detail-row"><td colSpan={17}>
                    <JobRunsTable jobId={j.job_id} jr={jobRunsCache[j.job_id]} />
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobRunsTable({ jobId, jr }: { jobId: string; jr: any }) {
  if (!jr) return <span className="loading-note">Loading runs&hellip;</span>;
  if (jr.error) return <span className="loading-note">Failed to load: {jr.error}</span>;
  return (
    <div className="detail-card">
      <h4>Job {jobId} &mdash; All Runs Today</h4>
      <table className="mini-table">
        <thead><tr><th>Activity</th><th>Start (UTC)</th><th>End (UTC)</th><th>Status</th><th>Rows</th><th>Run ID</th><th>Error</th></tr></thead>
        <tbody>
          {(jr.runs || []).map((r: any, i: number) => (
            <tr key={i}>
              <td>{r.activity_name || "-"}</td>
              <td className="datetime-cell">{r.actual_start || "-"}</td>
              <td className="datetime-cell">{r.actual_end || "-"}</td>
              <td className={`row-status ${cls(r.run_status)}`}>{r.run_status}</td>
              <td>{fmtNum(r.row_count)}</td>
              <td>{r.run_id || "-"}</td>
              <td>{r.error_msg || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobSideCards({ jobId, jd, onSaved }: { jobId: string; jd: any; onSaved: () => void }) {
  if (!jd) return <span className="loading-note">Loading&hellip;</span>;
  if (jd.error) return <span className="loading-note">Failed to load: {jd.error}</span>;

  return (
    <div className="job-side-grid">
      <ExpectedScheduleEditor jobId={jobId} jd={jd} onSaved={onSaved} />
      <div className="detail-card sched-card">
        <h4>Job {jobId} &mdash; Weekly Expected Run Time</h4>
        <table className="mini-table">
          <thead><tr><th>Day</th><th>Avg Start</th><th>Avg End</th><th>Runs (90d)</th></tr></thead>
          <tbody>
            {(jd.weekly_schedule || []).map((d: any, i: number) => (
              <tr key={i}><td>{(d.day || "").slice(0, 3)}</td><td>{d.avg_start || "-"}</td><td>{d.avg_end || "-"}</td><td>{d.run_count > 0 ? d.run_count : "\u2014"}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="detail-card volume-card">
        <h4>Job {jobId} &mdash; Volume (avg rows / duration)</h4>
        <table className="mini-table">
          <thead><tr><th>Day</th><th>Avg Rows</th><th>Avg Duration</th><th>Runs (90d)</th></tr></thead>
          <tbody>
            {(jd.volume || []).map((d: any, i: number) => (
              <tr key={i}><td>{(d.day || "").slice(0, 3)}</td><td>{fmtNum(d.avg_row_count)}</td><td>{fmtHrs(d.avg_duration_hrs)}</td><td>{d.run_count > 0 ? d.run_count : "\u2014"}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpectedScheduleEditor({ jobId, jd, onSaved }: { jobId: string; jd: any; onSaved: () => void }) {
  const sched = jd.expected_schedule || {};
  const source = sched.is_derived_from || (sched.is_user_edited ? "user-edited" : "history");
  const isCalendar = (sched.schedule_type || "").toLowerCase() === "calendar";

  if (isCalendar) return <ExpectedDateEditor jobId={jobId} sched={sched} source={source} onSaved={onSaved} />;

  const initialDays: string[] = (sched.expected_days || "").split(",").map((d: string) => d.trim()).filter(Boolean);
  const [days, setDays] = useState<string[]>(initialDays);
  const [start, setStart] = useState(sched.expected_start_time || "");
  const [end, setEnd] = useState(sched.expected_end_time || "");
  const [msg, setMsg] = useState("");
  const [msgErr, setMsgErr] = useState(false);

  async function save() {
    setMsg("Saving...");
    setMsgErr(false);
    const { ok } = await api.iberiaExpectedDays({
      job_id: jobId, expected_days: days.join(","),
      expected_start_time: start || null, expected_end_time: end || null,
    });
    if (ok) { setMsg("Saved"); onSaved(); } else { setMsgErr(true); setMsg("Save failed"); }
  }

  return (
    <div className="detail-card expected-editor">
      <h4>Expected Schedule <span className="source-note">Source: {source}</span></h4>
      <div className="expected-days">
        {DOW_NAMES.map((day) => (
          <label key={day}>
            <input type="checkbox" checked={days.includes(day)} onChange={(e) => setDays((cur) => (e.target.checked ? [...cur, day] : cur.filter((d) => d !== day)))} /> {day.slice(0, 3)}
          </label>
        ))}
      </div>
      <div className="expected-time-row">
        <label>Start (UTC) <input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label>End (UTC) <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        <button className="expected-save" onClick={(e) => { e.stopPropagation(); save(); }}>Save</button>
        <span className="expected-save-msg" style={msgErr ? { color: "#c8341f" } : undefined}>{msg}</span>
      </div>
    </div>
  );
}

function ExpectedDateEditor({ jobId, sched, source, onSaved }: { jobId: string; sched: any; source: string; onSaved: () => void }) {
  const initialDates: string[] = (sched.dates || []).map((d: any) => d.schedule_date || d).filter(Boolean);
  const [dates, setDates] = useState<string[]>(initialDates);
  const [newDate, setNewDate] = useState("");
  const [start, setStart] = useState(sched.expected_start_time || ((sched.dates || [])[0] || {}).expected_time || "");
  const [msg, setMsg] = useState("");
  const [msgErr, setMsgErr] = useState(false);

  function addDate() {
    if (!newDate || dates.includes(newDate)) return;
    setDates((cur) => [...cur, newDate]);
    setNewDate("");
  }

  async function save() {
    setMsg("Saving...");
    setMsgErr(false);
    const { ok } = await api.iberiaExpectedDates({ job_id: jobId, schedule_dates: [...dates].sort((a, b) => a.localeCompare(b)), expected_time: start || null });
    if (ok) { setMsg("Saved"); onSaved(); } else { setMsgErr(true); setMsg("Save failed"); }
  }

  return (
    <div className="detail-card expected-editor" data-calendar="1">
      <h4>Expected Calendar Dates <span className="source-note">Source: {source}</span></h4>
      <div className="expected-date-tools">
        <label>Date <DateInput value={newDate} onChange={(e) => setNewDate(e.target.value)} /></label>
        <label>Start (UTC) <input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <button className="expected-date-add" onClick={(e) => { e.stopPropagation(); addDate(); }}>Add Date</button>
      </div>
      <div className="expected-date-list">
        {dates.length === 0 ? <span className="loading-note">No dates selected.</span> : dates.map((d) => (
          <span key={d} className="expected-date-chip">
            {d}
            <button onClick={(e) => { e.stopPropagation(); setDates((cur) => cur.filter((x) => x !== d)); }}>&times;</button>
          </span>
        ))}
      </div>
      <div className="expected-time-row" style={{ marginTop: 10 }}>
        <button className="expected-save" onClick={(e) => { e.stopPropagation(); save(); }}>Save Dates</button>
        <span className="expected-save-msg" style={msgErr ? { color: "#c8341f" } : undefined}>{msg}</span>
      </div>
    </div>
  );
}
